// client/src/components/VtMDiceRoller.jsx

import React, { useState, useEffect } from "react";
import "./VtMDiceRoller.css";

const VtMDiceRoller = ({ socket, username, roomName }) => {
  const [numDice, setNumDice] = useState(5);
  const [targetNumber, setTargetNumber] = useState(6);
  const [rollResult, setRollResult] = useState(null);
  const [isRolling, setIsRolling] = useState(false);
  const [rollHistory, setRollHistory] = useState([]);

  const EVENT_NAME = "vtmRoll";

  // Listen for shared rolls (same pattern as Marvel)
  useEffect(() => {
    if (!socket) return;

    const onSharedRoll = (payload) => {
      // optional guard: only accept current room
      if (payload?.roomName && roomName && payload.roomName !== roomName) return;

      // Deduplicate by id (prevents double-add if server echoes back)
      setRollHistory((prev) => {
        if (payload?.id && prev.some((x) => x.id === payload.id)) return prev;

        return [
          {
            id: payload.id,
            by: payload.by || "Someone",
            input: payload.input,
            result: payload.result,
            timestamp: payload.timestamp ? new Date(payload.timestamp) : new Date(),
          },
          ...prev.slice(0, 9),
        ];
      });

      // Optional: show latest shared roll in the main results panel
      if (payload?.result) setRollResult(payload.result);
    };

    socket.on(EVENT_NAME, onSharedRoll);
    return () => socket.off(EVENT_NAME, onSharedRoll);
  }, [socket, roomName]);

  const rollDice = () => {
    setIsRolling(true);
    setRollResult(null);

    // Animate roll
    setTimeout(() => {
      const results = performRoll(numDice, targetNumber);

      // Local UI update
      setRollResult(results);
      setIsRolling(false);

      // Build payload (Marvel-style)
      const payload = {
        id: crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        roomName,
        by: username || "Someone",
        input: { numDice, targetNumber },
        result: results,
        timestamp: new Date().toISOString(),
      };

      // Add to local history immediately
      setRollHistory((prev) => [
        {
          id: payload.id,
          by: payload.by,
          input: payload.input,
          result: payload.result,
          timestamp: new Date(payload.timestamp),
        },
        ...prev.slice(0, 9),
      ]);

      // Broadcast to room (if available)
      if (socket && roomName) {
        socket.emit(EVENT_NAME, payload);
      }
    }, 500);
  };

  const performRoll = (diceCount, target) => {
    const allRolls = [];
    let totalSuccesses = 0;
    let totalBotches = 0;
    let diceToRoll = diceCount;
    let rollIndex = 0;

    // Initial roll
    while (diceToRoll > 0) {
      const roll = Math.floor(Math.random() * 10) + 1;
      const isSuccess = roll >= target;
      const isExplode = roll === 10;
      const isBotch = roll === 1;

      allRolls.push({
        value: roll,
        isSuccess,
        isExplode,
        isBotch,
        rollIndex: rollIndex++,
      });

      if (isSuccess) totalSuccesses++;
      if (isBotch) totalBotches++;
      if (isExplode) diceToRoll++; // Add one more die to roll
      diceToRoll--;
    }

    return {
      rolls: allRolls,
      totalSuccesses: totalSuccesses - totalBotches,
      targetNumber: target,
    };
  };

  const clearHistory = () => {
    setRollHistory([]);
    setRollResult(null);
  };

  const getDieClass = (roll) => {
    if (roll.isExplode) return "die-explode";
    if (roll.isBotch) return "die-botch";
    if (roll.isSuccess) return "die-success";
    return "die-failure";
  };

  return (
    <div className="vtm-dice-container">
      <div className="vtm-controls">
        <div className="control-group">
          <label htmlFor="num-dice" className="control-label">
            # of Dice:
          </label>
          <input
            id="num-dice"
            type="number"
            min="1"
            max="20"
            value={numDice}
            onChange={(e) =>
              setNumDice(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))
            }
            className="control-input"
            disabled={isRolling}
          />
        </div>

        <div className="control-group">
          <label htmlFor="target-number" className="control-label">
            Target:
          </label>
          <select
            id="target-number"
            value={targetNumber}
            onChange={(e) => setTargetNumber(parseInt(e.target.value))}
            className="control-select"
            disabled={isRolling}
          >
            {[1,2,3,4,5,6,7,8,9,10].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="vtm-roll-area">
        <button
          className={`vtm-roll-button ${isRolling ? "rolling" : ""}`}
          onClick={rollDice}
          disabled={isRolling}
        >
          {isRolling ? "Rolling..." : "Roll Dice"}
        </button>

        {rollResult && (
          <div className="vtm-results">
            <div className="successes-display">
              <div className="successes-label">Total Successes</div>
              <div
                className={`successes-value ${
                  rollResult.totalSuccesses > 0
                    ? "positive"
                    : rollResult.totalSuccesses < 0
                    ? "negative"
                    : "zero"
                }`}
              >
                {rollResult.totalSuccesses}
              </div>
            </div>

            <div className="dice-results">
              <div className="dice-results-label">
                Individual Rolls (Target: {rollResult.targetNumber}+)
              </div>
              <div className="dice-grid">
                {rollResult.rolls.map((roll, index) => (
                  <div
                    key={index}
                    className={`die ${getDieClass(roll)}`}
                    title={
                      roll.isExplode
                        ? "10 - Success + Explode!"
                        : roll.isBotch
                        ? "1 - Botch (subtracts 1 success)"
                        : roll.isSuccess
                        ? `${roll.value} - Success`
                        : `${roll.value} - Failure`
                    }
                  >
                    <span className="die-value">{roll.value}</span>
                    {roll.isExplode && <span className="die-badge explode">★</span>}
                    {roll.isBotch && <span className="die-badge botch">✕</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {rollHistory.length > 0 && (
        <div className="vtm-history">
          <div className="history-header">
            <h3>History</h3>
            <button className="clear-button" onClick={clearHistory}>
              Clear
            </button>
          </div>

          <div className="history-list">
            {rollHistory.map((item, index) => (
              <div key={item.id || index} className="history-item">
                <div className="history-info">
                  <span className="history-dice">
                    {(item.by ? `${item.by}: ` : "")}
                    {item.input?.numDice ?? "?"}d10 (TN {item.input?.targetNumber ?? "?"})
                  </span>
                </div>
                <div className="history-successes">
                  <span
                    className={`successes-count ${
                      item.result?.totalSuccesses > 0
                        ? "positive"
                        : item.result?.totalSuccesses < 0
                        ? "negative"
                        : "zero"
                    }`}
                  >
                    {item.result?.totalSuccesses ?? 0} Success
                    {(item.result?.totalSuccesses ?? 0) !== 1 ? "es" : ""}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VtMDiceRoller;
